import React, { useRef, useCallback, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PdfViewerComponent, Toolbar, Magnification, Navigation,
    LinkAnnotation, BookmarkView, ThumbnailView, Print,
    TextSelection, Annotation, TextSearch, FormFields,
    FormDesigner, Inject } from '@syncfusion/ej2-react-pdfviewer';
import fetchBase from "../utils/fetchBase";

export default function EmployeeDocumentViewerPage() {
    const { documentId } = useParams();
    const navigate = useNavigate();
    const viewerRef = useRef(null);
    const token = localStorage.getItem("jwt_token");
    const documentUrl = `${fetchBase}/api/document-hub/documents/${documentId}/file`;

    const saveTimeoutRef = useRef(null);
    const [saveStatus, setSaveStatus] = useState(null);
    const [dynamicInfo, setDynamicInfo] = useState(null);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        fetch(`${fetchBase}/api/document-hub/employee/my-hub`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(res => res.json())
            .then(data => {
                const allDocs = (data.sections || []).flatMap(s => s.documents || []);
                const thisDoc = allDocs.find(d => d.id === parseInt(documentId));
                if (thisDoc?.isDynamic) setDynamicInfo(thisDoc);
            })
            .catch(err => console.error("Failed to fetch document info:", err));
    }, [documentId]);

    const performSave = useCallback(async () => {
        if (!viewerRef.current) return;
        const startedAt = Date.now();
        try {
            const blob = await viewerRef.current.saveAsBlob();
            const formData = new FormData();
            formData.append("file", blob, "document.pdf");
            const res = await fetch(`${fetchBase}/api/document-hub/documents/${documentId}/save`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            const elapsed = Date.now() - startedAt;
            if (elapsed < 600) await new Promise(r => setTimeout(r, 600 - elapsed));
            setSaveStatus(res.ok ? "saved" : "error");
        } catch (err) {
            console.error("Autosave failed:", err);
            setSaveStatus("error");
        }
    }, [documentId, token]);

    const scheduleAutosave = useCallback(() => {
            if (dynamicInfo?.status === "COMPLETED") return;
            setSaveStatus("saving");
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(() => { performSave(); }, 400);
        }, [performSave, dynamicInfo]);

    const handleSend = async () => {
        if (!dynamicInfo) return;
        setSending(true);
        try {
            await performSave();
            const res = await fetch(`${fetchBase}/api/document-hub/dynamic/${dynamicInfo.dynamicAssignmentId}/send`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                navigate("/employee/document-hub");
            } else {
                alert("Failed to send. Please try again.");
            }
        } catch (err) {
            alert("Error sending document.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div style={{ minHeight: "100vh", background: "#f5f5f7" }}>
            <nav className="apple-global-nav">
                <div className="left-nav">
                    <img
                        src="/images/logoPlaceholder1.png"
                        alt="Logo"
                        className="apple-logo"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate("/employee/dashboard")}
                    />
                </div>
                <div className="center-nav">
                    <a href="/employee/document-hub">Document Hub</a>
                </div>
            </nav>
            <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "70px 20px 20px" }}>
                {dynamicInfo && (
                    <div style={{
                        background: "#fff", border: "1px solid #e0e0e0", borderRadius: "12px",
                        padding: "14px 18px", marginBottom: "14px",
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px"
                    }}>
                        <div style={{ minWidth: 0 }}>
                            <p style={{ fontWeight: 700, fontSize: "15px", margin: "0 0 2px", color: "#0071e3" }}>{dynamicInfo.assignmentTitle}</p>
                            {dynamicInfo.instructions && (
                                <p style={{ fontSize: "13px", color: "#6e6e73", margin: 0 }}>{dynamicInfo.instructions}</p>
                            )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
                            {saveStatus && (
                                <span style={{ fontSize: "12px", fontWeight: 600, color: saveStatus === "error" ? "#ff3b30" : "#6e6e73" }}>
                                    {saveStatus === "saving" ? "Saving..." : saveStatus === "saved" ? "Saved" : "Save failed"}
                                </span>
                            )}
                            {dynamicInfo.status === "COMPLETED" ? (
                                    <span style={{
                                        fontSize: "12px", fontWeight: 600, color: "#34c759",
                                        background: "#eafbf0", padding: "6px 14px", borderRadius: "8px"
                                    }}>
                                        Completed
                                    </span>
                                ) : (
                                    <button
                                        onClick={handleSend}
                                        disabled={sending}
                                        style={{
                                            background: "#0071e3", color: "#fff", border: "none",
                                            borderRadius: "8px", padding: "8px 18px", fontWeight: 600,
                                            fontSize: "13px", cursor: "pointer"
                                        }}
                                    >
                                        {sending ? "Sending..." : "Send"}
                                    </button>
                                )}
                        </div>
                    </div>
                )}
                <PdfViewerComponent
                    id="employee-pdf-viewer"
                    ref={viewerRef}
                    documentPath={documentUrl}
                    enableAnnotation={dynamicInfo?.status !== "COMPLETED"}
                    enableFormFields={dynamicInfo?.status !== "COMPLETED"}
                    enableFormDesigner={dynamicInfo?.status !== "COMPLETED"}
                    resourceUrl="https://cdn.syncfusion.com/ej2/26.2.11/dist/ej2-pdfviewer-lib"
                    ajaxRequestSettings={{ ajaxHeaders: [{ headerName: "Authorization", headerValue: `Bearer ${token}` }], withCredentials: false }}
                    annotationAdd={scheduleAutosave}
                    annotationRemove={scheduleAutosave}
                    annotationPropertiesChange={scheduleAutosave}
                    addSignature={scheduleAutosave}
                    formFieldFocusOut={scheduleAutosave}
                    style={{ height: "80vh", display: "block" }}
                >
                    <Inject services={[Toolbar, Magnification, Navigation, LinkAnnotation,
                        BookmarkView, ThumbnailView, Print, TextSelection, Annotation,
                        TextSearch, FormFields, FormDesigner]} />
                </PdfViewerComponent>
            </div>
        </div>
    );
}